import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

export const Navbar = ({ onConfigureClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [soluzioniOpen, setSoluzioniOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setSoluzioniOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const soluzioni = [
    { label: "Residenziale", href: "/residenziale" },
    { label: "PMI", href: "/pmi" },
  ];

  const navLinks = [
    { label: "Prodotti", href: "/prodotti", isPage: true },
    { label: "Calcolatore", href: "/calcolatore", isPage: true },
    { label: "Blog", href: "/blog", isPage: true },
    { label: "Chi Siamo", href: "/chi-siamo", isPage: true },
  ];

  const linkColor = scrolled ? "text-slate-600 hover:text-emerald-600" : "text-white/80 hover:text-white";
  const brandColor = scrolled ? "text-slate-900" : "text-white";

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-300 ${
        scrolled ? "nav-blur shadow-md" : "bg-slate-900/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 sm:h-28">
          <a href="#" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Fotovoltaico Chiavi in Mano" className="h-20 sm:h-24 md:h-28 w-auto object-contain" />
            <span className={`hidden sm:block text-lg sm:text-xl font-bold tracking-tight whitespace-nowrap ${brandColor}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              Fotovoltaico Chiavi in Mano
            </span>
          </a>

          <div className="hidden md:flex items-center gap-8">
            {/* Soluzioni dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setSoluzioniOpen(!soluzioniOpen)}
                className={`text-sm font-medium transition-colors duration-200 ${linkColor} cursor-pointer bg-transparent border-none flex items-center gap-1`}
                data-testid="nav-soluzioni-dropdown"
              >
                Soluzioni <ChevronDown className={`w-3.5 h-3.5 transition-transform ${soluzioniOpen ? "rotate-180" : ""}`} />
              </button>
              {soluzioniOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 py-2 w-48 z-50 animate-fade-in-up">
                  {soluzioni.map((s) => (
                    <button
                      key={s.href}
                      onClick={() => { navigate(s.href); setSoluzioniOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      data-testid={`nav-sol-${s.label.toLowerCase()}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => navigate(link.href)}
                className={`text-sm font-medium transition-colors duration-200 ${linkColor} cursor-pointer bg-transparent border-none`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={onConfigureClick}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale shadow-lg shadow-emerald-500/25"
              data-testid="nav-cta-button"
            >
              Calcola Preventivo
            </button>
          </div>

          <button
            className="md:hidden p-2 text-slate-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 glass rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-3 py-1">Soluzioni</p>
              {soluzioni.map((s) => (
                <button key={s.href} onClick={() => { navigate(s.href); setMobileOpen(false); }}
                  className="text-sm font-medium text-slate-700 hover:text-emerald-600 py-2 px-3 rounded-lg hover:bg-emerald-50 transition-colors text-left">
                  {s.label}
                </button>
              ))}
              <hr className="my-2 border-slate-100" />
              {navLinks.map((link) => (
                <button key={link.href} onClick={() => { navigate(link.href); setMobileOpen(false); }}
                  className="text-sm font-medium text-slate-700 hover:text-emerald-600 py-2 px-3 rounded-lg hover:bg-emerald-50 transition-colors text-left">
                  {link.label}
                </button>
              ))}
              <button onClick={() => { onConfigureClick(); setMobileOpen(false); }}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm mt-2">
                Calcola Preventivo
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
